<template>
  <printReceipt v-if="showPrintInvoice" :serviceInvoiceData="serviceInvoice" v-model="showPrintInvoice" />
  <main v-if="localShow" class="fixed inset-0 z-50 flex items-center justify-center font-syne px-4 sm:px-6"
    :style="{ fontFamily: `'Syne', sans-serif` }">
    <!-- Overlay -->
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close"></div>

    <!-- Modal Content -->
    <div
      class="relative bg-white dark:bg-gray-900/90 backdrop-blur-lg rounded-2xl shadow-xl w-full max-w-3xl sm:w-[693px] p-6 sm:p-8 flex flex-col max-h-[90vh] text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <!-- LOADING LOADING -->
      <div v-if="isLoading"
        class="absolute inset-0 z-50 flex flex-col justify-center items-center bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-2xl">
        <div class="w-12 h-12 border-4 border-t-transparent border-brand_orange2 rounded-full animate-spin"></div>
        <p class="mt-3 text-gray-800 dark:text-gray-100 font-medium">
          Loading...
        </p>
      </div>

      <!-- Header -->
      <div
        class="flex justify-between items-center border-b-2 pb-2 mb-7 border-[#e87055] dark:border-[#f97316] transition-colors duration-300">
        <h3 class="text-xl font-semibold">View Service Invoice</h3>
        <button
          class="transition bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white font-bold px-3 py-1 rounded-full shadow-lg focus:outline-none"
          @click="close">
          ✕
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto space-y-6">
        <!-- General Information -->
        <div class="mb-6">
          <div class="flex items-baseline mb-4">
            <h4 class="font-semibold mr-3 text-lg dark:text-gray-200">
              General Information
            </h4>
            <div class="flex-1 h-0.5 bg-[#e87055] dark:bg-[#f97316]"></div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Invoice # -->
            <div>
              <span class="block font-semibold dark:text-gray-300">Invoice #:
              </span>
              <span v-if="!isEditMode" class="block text-base dark:text-gray-100">{{ serviceInvoice?.invoice_number
              }}</span>
              <input v-else v-model="newData.invoice_number"
                class="block text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600" />
            </div>

            <!-- Invoice Date -->
            <div>
              <span class="block font-semibold dark:text-gray-300">Invoice Date:
              </span>
              <span v-if="!isEditMode" class="block text-base dark:text-gray-100">{{
                formatDate(serviceInvoice?.invoice_date) }}</span>
              <input v-else type="date" v-model="newData.invoice_date"
                class="block text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600" />
            </div>

            <!-- Payee -->
            <div>
              <span class="block font-semibold dark:text-gray-300">Payee:
              </span>
              <span v-if="!isEditMode" class="block text-base dark:text-gray-100">{{ serviceInvoice?.payee?.sold_to
              }}</span>
              <input v-else v-model="newData.soldTo"
                class="block text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600" />
            </div>

            <!-- TIN -->
            <div>
              <span class="block font-semibold dark:text-gray-300">TIN: </span>
              <span v-if="!isEditMode" class="block text-base dark:text-gray-100">{{ serviceInvoice?.payee?.tin
              }}</span>
              <input v-else v-model="newData.tin"
                class="block text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600" />
            </div>

            <!-- Address + Description -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 col-span-1 sm:col-span-2">
              <div>
                <span class="block font-semibold dark:text-gray-300">Address:
                </span>
                <span v-if="!isEditMode" class="block text-base dark:text-gray-100">{{ serviceInvoice?.payee?.address
                }}</span>
                <textarea v-else rows="3" v-model="newData.address"
                  class="block text-base border rounded px-2 py-1 w-full resize-none dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"></textarea>
              </div>
              <div>
                <span class="block font-semibold dark:text-gray-300">Description:
                </span>
                <span v-if="!isEditMode" class="block text-base dark:text-gray-100">{{
                  serviceInvoice?.invoiceItems?.[0]?.description || "N/A"
                }}</span>
                <textarea v-else rows="3" v-model="newData.invoice_items[0].description"
                  class="block text-base border rounded px-2 py-1 w-full resize-none dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Information -->
        <div class="mb-6">
          <!-- Header -->
          <div class="flex flex-col sm:flex-row items-baseline mb-4">
            <h4 class="font-semibold text-lg dark:text-gray-200 mb-2 sm:mb-0 sm:mr-3">
              Payment Information
            </h4>
            <div class="flex-1 h-0.5 bg-[#e87055] dark:bg-[#f97316]"></div>
          </div>

          <!-- Fields Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            <!-- Mode of Payment -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Mode of Payment:</span>
              <span v-if="!isEditMode" class="text-base dark:text-gray-100 truncate">
                {{ serviceInvoice?.mode_of_payment || "—" }}
              </span>
              <select v-else v-model="newData.mode_of_payment"
                class="text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#e87055]">
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
              </select>
            </div>

            <!-- With Withholding -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">With Withholding:</span>
              <span v-if="!isEditMode" class="text-base dark:text-gray-100">
                {{ serviceInvoice?.payee.with_withholding_tax ? "Yes" : "No" }}
              </span>
              <select v-else v-model="newData.withWithholding"
                class="text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <!-- VATable Sales -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">VATable Sales:</span>
              <span v-if="!isEditMode" class="text-base dark:text-gray-100 truncate">
                {{ formatPeso(serviceInvoice?.VATable_sales) }}
              </span>
              <input v-else type="number" v-model.number="newData.VATable_sales"
                class="text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#e87055]" />
            </div>

            <!-- Zero Rated Sales -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Zero Rated Sales:</span>
              <span v-if="!isEditMode" class="text-base dark:text-gray-100 truncate">
                {{ formatPeso(serviceInvoice?.zero_rated_sales) }}
              </span>
              <input v-else type="number" v-model.number="newData.zero_rated_sales"
                class="text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#e87055]" />
            </div>

            <!-- VAT-Exempt Sales -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">VAT-Exempt Sales:</span>
              <span v-if="!isEditMode" class="text-base dark:text-gray-100 truncate">
                {{ formatPeso(serviceInvoice?.vat_Exempt_sales) }}
              </span>
              <input v-else type="number" v-model.number="newData.vat_Exempt_sales"
                class="text-base border rounded px-2 py-1 w-full dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#e87055]" />
            </div>

            <!-- VAT Amount -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">VAT Amount:</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.vatAmount ?? 0
                      : serviceInvoice?.vat_amount
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.vatAmount ?? 0)
                    : formatPeso(serviceInvoice?.vat_amount)
                }}
              </span>
            </div>

            <!-- Amount -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Amount:</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.amount ?? 0
                      : serviceInvoice?.amount
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.amount ?? 0)
                    : formatPeso(serviceInvoice?.amount)
                }}
              </span>
            </div>

            <!-- Total Sales (VAT Inclusive) -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Total Sales (VAT Inclusive):</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.totalAmountDue ?? 0
                      : serviceInvoice?.total_sales_vat_inclusive
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.totalAmountDue ?? 0)
                    : formatPeso(serviceInvoice?.total_sales_vat_inclusive)
                }}
              </span>
            </div>

            <!-- Less SC/PWD Discount -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Less SC/PWD Discount:</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.lessScPwdDiscount ?? 0
                      : serviceInvoice?.less_sc_pwd_discount
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.lessScPwdDiscount ?? 0)
                    : formatPeso(serviceInvoice?.less_sc_pwd_discount)
                }}
              </span>
            </div>

            <!-- Less VAT -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Less VAT:</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.lessVat ?? 0
                      : serviceInvoice?.less_vat
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.lessVat ?? 0)
                    : formatPeso(serviceInvoice?.less_vat)
                }}
              </span>
            </div>

            <!-- Less Withholding Tax -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Less Withholding Tax:</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.lessWithholding ?? 0
                      : serviceInvoice?.less_withholding_tax
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.lessWithholding ?? 0)
                    : formatPeso(serviceInvoice?.less_withholding_tax)
                }}
              </span>
            </div>

            <!-- Net of VAT -->
            <div class="flex flex-col">
              <span class="font-semibold dark:text-gray-300 mb-1">Net of VAT:</span>
              <span class="text-base dark:text-gray-100" :class="{
                'text-green-600 dark:text-green-400':
                  Number(
                    isEditMode
                      ? computedResults?.netOfVat ?? 0
                      : serviceInvoice?.amount_net_of_vat
                  ) !== 0,
              }">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.netOfVat ?? 0)
                    : formatPeso(serviceInvoice?.amount_net_of_vat)
                }}
              </span>
            </div>

            <!-- Divider -->
            <div class="col-span-1 sm:col-span-2 border-t border-gray-300 dark:border-gray-600 mt-2"></div>

            <!-- Total Amount Due -->
            <div class="col-span-1 sm:col-span-2 flex justify-between font-bold text-lg mt-2">
              <span>Total Amount Due:</span>
              <span class="text-green-600 dark:text-green-400">
                {{
                  isEditMode
                    ? formatPeso(computedResults?.totalAmountDue ?? 0)
                    : formatPeso(serviceInvoice?.total_amount_due)
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="mt-4 border-t pt-4 flex flex-col sm:flex-row justify-end sm:space-x-3 space-y-3 sm:space-y-0 border-gray-300 dark:border-gray-600 transition-colors duration-300">
        <button v-if="!isEditMode" :disabled="userData.role === 'viewing'" @click="toggleEditMode"
          class="w-full sm:w-28 px-4 py-2 rounded-md bg-[#e87055] text-white hover:bg-[#d75b40] dark:bg-orange-600 dark:hover:bg-orange-500 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed">
          Edit
        </button>

        <div v-else class="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <button @click="toggleEditMode"
            class="w-full sm:w-28 px-4 py-2 rounded-md bg-gray-300 text-black hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 transition-colors duration-300">
            Cancel
          </button>
          <button @click="saveChanges"
            class="w-full sm:w-28 px-4 py-2 rounded-md bg-[#f59e0b] text-white hover:bg-[#d97706] dark:bg-yellow-600 dark:hover:bg-yellow-500 transition-colors duration-300">
            Save
          </button>
        </div>

        <button v-if="!isEditMode" :disabled="userData.role === 'viewing'" @click="showPrintInvoice = true"
          class="w-full sm:w-28 px-4 py-2 rounded-md bg-[#f59e0b] text-white hover:bg-[#d97706] dark:bg-yellow-600 dark:hover:bg-yellow-500 transition-colors duration-300">
          Print
        </button>
      </div>
    </div>
  </main>
</template>

<script>
import { ref, watch, inject, reactive, computed } from "vue";
import printReceipt from "./printReceipt.vue";
import loading from "./loading.vue";

// Vuex
import { useStore } from "vuex";

export default {
  components: { printReceipt, loading },
  props: {
    showViewServiceInvoice: { type: Boolean, required: true },
    invoiceId: { type: [String, Number], required: true },
  },
  setup(props, { emit }) {
    const axios = inject("$axios");
    const isLoading = ref(false);
    const serviceInvoice = ref(null);
    const localShow = ref(props.showViewServiceInvoice);
    const isEditMode = ref(false);

    const store = useStore();
    const userData = computed(() => store.getters.getUserData);

    const close = () => {
      isEditMode.value = false;
      emit("update:showViewServiceInvoice", false);
    };

    const selectedServiceInvoice = async (id) => {
      if (!id) return;
      isLoading.value = true;
      try {
        const res = await axios.get(`/api/invoice/${id}`);
        serviceInvoice.value = res.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        isLoading.value = false;
      }
    };

    watch(
      () => [props.invoiceId, props.showViewServiceInvoice],
      ([id, show]) => {
        localShow.value = show;
        if (id && show) selectedServiceInvoice(id);
      },
      { immediate: true }
    );

    const formatPeso = (value) => {
      if (!value) return "₱ 0.00";
      return `₱ ${parseFloat(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    const formatDate = (date) => {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    // EDIT MODE

    const newData = reactive({
      id: null, // transaction id
      payeeId: null, // payee id
      invoice_number: "",
      soldTo: "",
      tin: "",
      address: "",
      terms: "",
      osca_pwd_id_no: "",
      sc_pwd_signature: "",
      invoice_date: "",
      mode_of_payment: "Cash",
      withWithholding: false,
      VATable_sales: 0,
      zero_rated_sales: 0,
      vat_Exempt_sales: 0,
      invoice_items: [],
    });

    const loadInvoiceForEdit = () => {
      if (!serviceInvoice.value) return;

      // Transaction & Payee IDs
      newData.id = serviceInvoice.value.id;
      newData.payeeId = serviceInvoice.value.payee?.id || null;

      // General Information
      newData.invoice_number = serviceInvoice.value.invoice_number || "";
      newData.invoice_date = serviceInvoice.value.invoice_date || "";
      newData.soldTo = serviceInvoice.value.payee?.sold_to || "";
      newData.tin = serviceInvoice.value.payee?.tin || "";
      newData.address = serviceInvoice.value.payee?.address || "";
      newData.terms = serviceInvoice.value.terms || "";
      newData.osca_pwd_id_no = serviceInvoice.value.osca_pwd_id_no || "";
      newData.sc_pwd_signature = serviceInvoice.value.sc_pwd_signature || "";

      // Payment & Sales Info
      newData.mode_of_payment = serviceInvoice.value.mode_of_payment || "Cash";
      newData.withWithholding =
        !!serviceInvoice.value.payee?.with_withholding_tax;

      // VAT / Sales mapping (frontend ↔ backend)
      newData.VATable_sales = parseFloat(
        serviceInvoice.value.VATable_sales || 0
      );
      newData.zero_rated_sales = parseFloat(
        serviceInvoice.value.zero_rated_sales || 0
      );
      newData.vat_Exempt_sales = parseFloat(
        serviceInvoice.value.vat_Exempt_sales || 0
      );

      // Invoice Items
      newData.invoice_items = (serviceInvoice.value.invoiceItems || []).map(
        (item) => ({
          id: item.id,
          description: item.description || "",
          quantity: item.quantity || 0,
          cost: item.cost || 0,
          total: item.total || 0,
          transactionId: item.transactionId || newData.id,
        })
      );
    };

    const toggleEditMode = () => {
      isEditMode.value = !isEditMode.value;
      if (isEditMode.value) loadInvoiceForEdit();
    };

    const saveChanges = async () => {
      if (!serviceInvoice.value) return;

      isLoading.value = true;

      try {
        const payload = {
          id: serviceInvoice.value.id, // make sure backend knows which invoice to update
          payeeId: serviceInvoice.value.payee.id, // include payee ID
          ...newData,
          invoice_items: newData.invoice_items.map((item) => ({
            ...item,
          })),
        };

        await axios.put("/api/invoice/update", payload);
        console.log("Saved successfully!");

        // reload invoice to see all backend-calculated changes
        await selectedServiceInvoice(serviceInvoice.value.id);

        isEditMode.value = false;
      } catch (error) {

        console.error("Error saving invoice:", error);

      } finally {

        isLoading.value = false;

      }
    };

    const showPrintInvoice = ref(false);
    const computedResults = ref({});

    const computeTransaction = async (data) => {
      if (
        !data.vatableSalesRaw &&
        !data.zeroRatedSales &&
        !data.vatExemptSales
      ) {
        computedResults.value = {};
        return;
      }

      try {
        const res = await axios.get(
          "/api/invoice/get/transaction/computation",
          {
            params: data,
          }
        );
        computedResults.value = res.data.data || {};
        console.log("Computed Result:", computedResults.value);
      } catch (err) {
        console.error("Error computing transaction:", err);
      }
    };

    watch(
      () => [
        newData.VATable_sales,
        newData.zero_rated_sales,
        newData.vat_Exempt_sales,
        newData.withWithholding,
      ],
      ([vatable, zeroRated, exempt, withHold]) => {
        if (vatable === null && zeroRated === null && exempt === null) return;

        computeTransaction({
          vatableSalesRaw: parseFloat(vatable || 0),
          zeroRatedSales: parseFloat(zeroRated || 0),
          vatExemptSales: parseFloat(exempt || 0),
          withWithholding: withHold,
        });
      },
      { immediate: true }
    );

    return {
      computeTransaction,
      computedResults,
      showPrintInvoice,
      newData,
      saveChanges,
      isEditMode,
      toggleEditMode,
      localShow,
      isLoading,
      serviceInvoice,
      close,
      formatPeso,
      formatDate,

      // UserData
      userData,
    };
  },
};
</script>
